/**
 * learning-artifact-registry.ts
 *
 * Quarantined Learning Artifact Store, Batch Manifest Validator,
 * Resumable Cascade Lease Manager, and Disjoint LKG Ancestry Resolver.
 */

import { sha256Sync } from '../../core/genesis-provisioner.js';
import {
  LearningArtifact,
  ArtifactStatus,
  ArtifactTombstone,
  DerivedAssetDependency,
  CandidateChange,
  ChannelRelease,
  LearningScope,
  CascadeLease,
  LearningBatchManifest,
} from './learning-contracts.js';
import { PrivacySanitizer } from './privacy-sanitizer.js';
import { UserRecognitionEngine } from './user-recognition-engine.js';

export interface RegisterArtifactParams {
  readonly sourceRequestId: string;
  readonly sourceChannelId: string;
  readonly sourcePluginId?: string;
  readonly tenantId?: string;
  readonly artifactType: LearningArtifact['artifactType'];
  readonly input?: string;
  readonly output?: string;
  readonly expectedBehavior?: string;
  readonly correction?: string;
  readonly allowedScopes: readonly string[];
  readonly deniedScopes?: readonly string[];
  readonly sensitivity?: LearningArtifact['sensitivity'];
  readonly createdByPrincipalId: string;
}

export class LearningArtifactRegistry {
  private readonly artifacts = new Map<string, LearningArtifact>();
  private readonly tombstones = new Map<string, ArtifactTombstone>();
  private readonly derivedDependencies: DerivedAssetDependency[] = [];
  private readonly candidateChanges = new Map<string, CandidateChange>();
  private readonly channelReleases = new Map<string, ChannelRelease>();
  private readonly activeLeases = new Map<string, CascadeLease>();
  private readonly batchManifests = new Map<string, LearningBatchManifest>();

  private readonly defaultLeaseTtlMs = 10_000;

  constructor(
    private readonly privacySanitizer: PrivacySanitizer = new PrivacySanitizer(),
    private readonly recognitionEngine: UserRecognitionEngine = new UserRecognitionEngine()
  ) {}

  /**
   * Evaluates if a principal / channel / plugin context can access a learning artifact.
   */
  canUseLearningArtifact(scope: LearningScope, artifact: LearningArtifact): boolean {
    if (artifact.status !== 'approved' && artifact.status !== 'active' && artifact.status !== 'canary') {
      return false;
    }

    if (artifact.tenantId !== scope.tenantId) {
      return false;
    }

    const currentScopes = [
      `tenant:${scope.tenantId}`,
      scope.pluginId ? `plugin:${scope.pluginId}` : '',
      scope.channelId ? `channel:${scope.channelId}` : '',
      scope.principalScope ? `principal:${scope.principalScope}` : '',
    ].filter(Boolean);

    if (artifact.deniedScopes.some((ds) => currentScopes.includes(ds))) {
      return false;
    }

    return artifact.allowedScopes.some((as) => as === 'global:learning-approved' || currentScopes.includes(as));
  }

  /**
   * Registers a new sanitized learning candidate into QUARANTINED status.
   */
  registerCandidateArtifact(params: RegisterArtifactParams): LearningArtifact {
    const tenantId = params.tenantId || 'tenant-default';

    const inScan = params.input ? this.privacySanitizer.sanitize(params.input) : undefined;
    const outScan = params.output ? this.privacySanitizer.sanitize(params.output) : undefined;
    const corrScan = params.correction ? this.privacySanitizer.sanitize(params.correction) : undefined;

    const piiDetected = Boolean(inScan?.piiDetected || outScan?.piiDetected || corrScan?.piiDetected);
    const secretsDetected = Boolean(inScan?.secretsDetected || outScan?.secretsDetected || corrScan?.secretsDetected);
    const poisonPatternDetected = Boolean(inScan?.poisonPatternDetected || outScan?.poisonPatternDetected || corrScan?.poisonPatternDetected);

    const artifactId = `art-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const createdByHmac = this.privacySanitizer.generateKeyVersionedHmac('analytics', params.createdByPrincipalId, tenantId);

    const artifact: LearningArtifact = {
      artifactId,
      sourceRequestId: params.sourceRequestId,
      sourceChannelId: params.sourceChannelId,
      sourcePluginId: params.sourcePluginId,
      tenantId,
      artifactType: params.artifactType,
      sanitizedInput: inScan?.sanitizedText || params.input,
      sanitizedOutput: outScan?.sanitizedText || params.output,
      expectedBehavior: params.expectedBehavior,
      correction: corrScan?.sanitizedText || params.correction,
      allowedScopes: params.allowedScopes,
      deniedScopes: params.deniedScopes || [],
      sensitivity: params.sensitivity || 'internal',
      piiDetected,
      secretsDetected,
      poisonPatternDetected,
      status: 'quarantined',
      derivedAssetIds: [],
      createdByPrincipalId: params.createdByPrincipalId,
      createdByHmac,
      createdAt: new Date().toISOString(),
    };

    this.artifacts.set(artifactId, artifact);
    return artifact;
  }

  /**
   * Moderator review and approval of a quarantined artifact.
   * Enforces Anti-Collusion (reviewedBy !== createdBy) and probation double-approval.
   */
  reviewAndApprove(artifactId: string, reviewerId: string, secondReviewerId?: string): LearningArtifact {
    const artifact = this.artifacts.get(artifactId);
    if (!artifact) throw new Error(`Artifact ${artifactId} not found`);

    if (artifact.status !== 'quarantined' && artifact.status !== 'reviewed') {
      throw new Error(`Artifact ${artifactId} cannot be approved from status ${artifact.status}`);
    }

    // Anti-Collusion: Self-Review Check
    if (artifact.createdByPrincipalId === reviewerId) {
      throw new Error(`[AntiCollusion] Self-review forbidden: Reviewer ${reviewerId} cannot approve own contribution`);
    }

    const creatorProfile = this.recognitionEngine.getProfile(artifact.createdByPrincipalId, artifact.tenantId);

    if (creatorProfile.reputationStanding === 'probation') {
      if (!secondReviewerId) {
        const halfApproved: LearningArtifact = {
          ...artifact,
          status: 'reviewed',
          reviewedBy: reviewerId,
          reviewedAt: new Date().toISOString(),
        };
        this.artifacts.set(artifactId, halfApproved);
        return halfApproved;
      }

      if (secondReviewerId === reviewerId || secondReviewerId === artifact.createdByPrincipalId) {
        throw new Error(`[AntiCollusion] Second reviewer must be distinct from first reviewer and author`);
      }
    }

    const approved: LearningArtifact = {
      ...artifact,
      status: 'approved',
      reviewedBy: reviewerId,
      secondReviewedBy: secondReviewerId,
      reviewedAt: new Date().toISOString(),
    };

    this.artifacts.set(artifactId, approved);

    this.recognitionEngine.awardPoints({
      principalId: artifact.createdByPrincipalId,
      tenantId: artifact.tenantId,
      sourceEventId: artifactId,
      eventType: artifact.artifactType === 'knowledge_correction' ? 'correction_approved' : 'feedback_validated',
      points: artifact.artifactType === 'knowledge_correction' ? 50 : 20,
      reason: `Verified & Approved ${artifact.artifactType}`,
      reviewerId,
    });

    return approved;
  }

  /**
   * Rejection of an artifact by moderator with reputation penalty for malicious submissions.
   */
  reviewAndReject(artifactId: string, reviewerId: string, reason: string, isMalicious: boolean = false): LearningArtifact {
    const artifact = this.artifacts.get(artifactId);
    if (!artifact) throw new Error(`Artifact ${artifactId} not found`);

    const rejected: LearningArtifact = {
      ...artifact,
      status: 'rejected',
      reviewedBy: reviewerId,
      rejectionReason: reason,
      reviewedAt: new Date().toISOString(),
    };

    this.artifacts.set(artifactId, rejected);

    if (isMalicious || artifact.poisonPatternDetected) {
      this.recognitionEngine.transitionReputation({
        principalId: artifact.createdByPrincipalId,
        tenantId: artifact.tenantId,
        standing: 'probation',
        penaltyPoints: 50,
        reasonCodes: ['poisoned_artifact_submission', reason],
        decidedBy: 'reviewer',
        decidedByPrincipalId: reviewerId,
      });
    }

    return rejected;
  }

  linkDerivedAsset(sourceArtifactId: string, derivedAssetId: string, derivedType: DerivedAssetDependency['derivedType']): void {
    const artifact = this.artifacts.get(sourceArtifactId);
    if (!artifact) return;

    const contentHash = sha256Sync(artifact.sanitizedInput || artifact.correction || '');

    this.derivedDependencies.push({
      derivedAssetId,
      sourceArtifactId,
      sourceContentHash: contentHash,
      derivedType,
      status: 'active',
      createdAt: new Date().toISOString(),
    });

    const updatedDerived = Array.from(new Set([...artifact.derivedAssetIds, derivedAssetId]));
    this.artifacts.set(sourceArtifactId, {
      ...artifact,
      derivedAssetIds: updatedDerived,
    });
  }

  /**
   * Acquires or safely resumes a CascadeLease for transactional retraction.
   */
  acquireLease(artifactId: string, ownerId: string, now: number = Date.now()): CascadeLease {
    const existing = this.activeLeases.get(artifactId);
    if (existing && new Date(existing.expiresAt).getTime() > now) {
      if (existing.ownerId !== ownerId) {
        throw new Error(`[CascadeLease] Artifact ${artifactId} is locked by ${existing.ownerId} until ${existing.expiresAt}`);
      }
      return existing;
    }

    const lease: CascadeLease = {
      leaseId: `lease-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      artifactId,
      ownerId,
      acquiredAt: new Date(now).toISOString(),
      expiresAt: new Date(now + this.defaultLeaseTtlMs).toISOString(),
      heartbeatAt: new Date(now).toISOString(),
    };

    this.activeLeases.set(artifactId, lease);
    return lease;
  }

  releaseLease(artifactId: string): void {
    this.activeLeases.delete(artifactId);
  }

  /**
   * 10-Step Transactional Tombstone Cascade with Disjoint LKG Ancestry Resolution.
   */
  async retractArtifactWithCascade(
    artifactId: string,
    reason: ArtifactTombstone['reason'],
    ownerId: string = 'master-admin',
    now: number = Date.now()
  ): Promise<ArtifactTombstone> {
    const artifact = this.artifacts.get(artifactId);
    if (!artifact) throw new Error(`Artifact ${artifactId} not found`);

    // 1. Acquire / Resume Lease
    this.acquireLease(artifactId, ownerId, now);

    try {
      // 2. Mark artifact RETRACTING
      this.artifacts.set(artifactId, {
        ...artifact,
        status: 'retracting',
      });

      // 3. Create initial tombstone
      const contentHash = sha256Sync(artifact.sanitizedInput || artifact.correction || artifactId);

      // 4. Resolve derived candidate/RAG assets
      const affectedAssetIds = new Set<string>(artifact.derivedAssetIds);
      for (const dep of this.derivedDependencies) {
        if (dep.sourceArtifactId === artifactId) {
          affectedAssetIds.add(dep.derivedAssetId);
          (dep as any).status = 'invalidated';
        }
      }

      // 5. Mark derived assets INVALIDATED / rolled_back
      for (const assetId of affectedAssetIds) {
        const candidate = this.candidateChanges.get(assetId);
        if (candidate) {
          this.candidateChanges.set(assetId, {
            ...candidate,
            status: 'rolled_back',
          });
        }
      }

      // 6 & 7. Identify affected active releases & resolve genuinely disjoint LKG
      const rolledBackReleaseIds: string[] = [];
      let revertedToReleaseId: string | undefined;
      let enteredSafeDegradedMode = false;

      for (const [relId, release] of this.channelReleases.entries()) {
        const isContaminated =
          release.rootArtifactIds.includes(artifactId) ||
          (release.candidateId && affectedAssetIds.has(release.candidateId));

        if (isContaminated && release.isCurrentActive) {
          rolledBackReleaseIds.push(relId);

          // Find disjoint LKG release (complete ancestry disjointness)
          const history = Array.from(this.channelReleases.values())
            .filter((r) => r.channelId === release.channelId && r.tenantId === release.tenantId && r.releaseId !== relId)
            .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

          const cleanDisjointLkg = history.find(
            (r) => !r.rootArtifactIds.includes(artifactId) && (!r.candidateId || !affectedAssetIds.has(r.candidateId))
          );

          // 8. Atomically switch pointers or enter safe degraded mode
          this.channelReleases.set(relId, {
            ...release,
            isCurrentActive: false,
            isRolledBack: true,
            rolledBackAt: new Date(now).toISOString(),
            rollbackReason: `Cascaded Retraction of Artifact ${artifactId} (${reason})`,
          });

          if (cleanDisjointLkg) {
            revertedToReleaseId = cleanDisjointLkg.releaseId;
            this.channelReleases.set(cleanDisjointLkg.releaseId, {
              ...cleanDisjointLkg,
              isCurrentActive: true,
              isRolledBack: false,
            });
          } else {
            // No disjoint LKG exists -> Enter safe degraded mode
            enteredSafeDegradedMode = true;
          }
        }
      }

      // 9 & 10. Mark RETRACTED, write tombstone, and release lease
      this.artifacts.set(artifactId, {
        ...artifact,
        status: 'retracted',
      });

      const tombstone: ArtifactTombstone = {
        artifactId,
        contentHash,
        tenantId: artifact.tenantId,
        reason,
        cascadeSummary: {
          invalidatedDerivedAssetIds: Array.from(affectedAssetIds),
          rolledBackReleaseIds,
          revertedToReleaseId,
          enteredSafeDegradedMode,
        },
        createdAt: new Date(now).toISOString(),
      };

      this.tombstones.set(artifactId, tombstone);
      return tombstone;
    } finally {
      this.releaseLease(artifactId);
    }
  }

  /**
   * Pre-ingestion batch manifest integrity check.
   */
  validateBatchManifest(manifest: LearningBatchManifest): { valid: boolean; violations: readonly string[] } {
    const violations: string[] = [];

    // Duplicate hash check
    const seenHashes = new Set<string>();
    for (const hash of manifest.contentHashes) {
      if (seenHashes.has(hash)) {
        violations.push(`Duplicate content hash detected in batch: ${hash}`);
      }
      seenHashes.add(hash);
    }

    if (manifest.artifactIds.length !== manifest.contentHashes.length) {
      violations.push('Artifact count does not match content hash count');
    }

    this.batchManifests.set(manifest.batchId, manifest);
    return { valid: violations.length === 0, violations };
  }

  getArtifact(artifactId: string): LearningArtifact | undefined {
    return this.artifacts.get(artifactId);
  }

  getTombstone(artifactId: string): ArtifactTombstone | undefined {
    return this.tombstones.get(artifactId);
  }

  listQuarantinedArtifacts(tenantId?: string): readonly LearningArtifact[] {
    return Array.from(this.artifacts.values()).filter(
      (a) => (a.status === 'quarantined' || a.status === 'reviewed') && (!tenantId || a.tenantId === tenantId)
    );
  }

  saveRelease(release: ChannelRelease): void {
    this.channelReleases.set(release.releaseId, release);
  }

  saveCandidate(candidate: CandidateChange): void {
    this.candidateChanges.set(candidate.candidateId, candidate);
  }
}
